import Button from '@/components/common/Button'
import { H4, P } from '@/components/common/Typo'
import useAppContext from '@/hooks/useAppContext'
import { promotionAppliedOnTypes, promotionTypes } from '@/utils/promotion'
import { TFnType, useTranslate } from '@tolgee/react'
import { ArrowRightIcon, Card, cn } from 'oddle-shop-monorepo.design-system'
import React from 'react'

// Define amount types locally since the import is not available
const amountTypes = {
  percentage: 0,
  dollar: 1,
}

// TODO: move to tool if there are other use case for this
const getChinesePercentageDiscountAmount = (amount: number): number => {
  const reverse = 100 - amount
  return reverse % 10 === 0 ? reverse / 10 : Math.floor(reverse)
}

export const getPromotionAvailabilityText = ({
  t,
  promotion,
}: {
  t: TFnType
  promotion: {
    appliedOn: number
  }
}): string => {
  switch (promotion.appliedOn) {
    case promotionAppliedOnTypes.pickup:
      return t('Valid for Pickup only!')
    case promotionAppliedOnTypes.delivery:
      return t('Valid for Delivery only!')
    case promotionAppliedOnTypes.both:
      return t('Valid for Pickup and Delivery!')
    default:
      return ''
  }
}

const getBannerHeaderText = ({
  t,
  resultAmountType,
  formattedDiscountAmount,
}: {
  t: TFnType
  resultAmountType: number
  formattedDiscountAmount: {
    [key: string]: string | number | bigint | boolean | Date | null | undefined
  }
}): string => {
  return resultAmountType === amountTypes.percentage
    ? t('{percentageDiscountAmount}%', formattedDiscountAmount)
    : t('{dollarDiscountAmount}', formattedDiscountAmount)
}

export const getPromotionBanner = ({
  t,
  promotion,
  formatMoney,
  PromotionBannerHeader = DefaultPromotionBannerHeader,
  PromotionBannerSubHeader = DefaultPromotionBannerSubHeader,
}: {
  t: TFnType
  promotion: Promotion
  formatMoney: (amount: number) => string
  PromotionBannerHeader?: React.ComponentType<{
    color?: string
    children?: React.ReactNode
  }>
  PromotionBannerSubHeader?: React.ComponentType<{ children?: React.ReactNode }>
}): JSX.Element => {
  const percentageDiscountAmount = promotion.resultAmount
  const chinesePercentageDiscountAmount = getChinesePercentageDiscountAmount(
    promotion.resultAmount
  )
  const dollarDiscountAmount = formatMoney(promotion.resultAmount)
  const resultAmountType = promotion.resultAmountType
  const formattedDiscountAmount = {
    percentageDiscountAmount,
    chinesePercentageDiscountAmount,
    dollarDiscountAmount,
  }
  switch (promotion.promotionResultType) {
    case promotionTypes.applySelectedItem:
      return (
        <React.Fragment>
          <PromotionBannerHeader>
            {getBannerHeaderText({
              t,
              resultAmountType,
              formattedDiscountAmount,
            })}
          </PromotionBannerHeader>
          <PromotionBannerSubHeader>
            {resultAmountType === amountTypes.percentage
              ? t(
                  '{percentageDiscountAmount} Off Selected Item(s)!',
                  formattedDiscountAmount
                )
              : t(
                  '{dollarDiscountAmount} Off Selected Item(s)!',
                  formattedDiscountAmount
                )}
          </PromotionBannerSubHeader>
        </React.Fragment>
      )
    case promotionTypes.minimumOrder:
      return (
        <React.Fragment>
          <PromotionBannerHeader>
            {t('{minOrderOverridenAmount}', {
              minOrderOverridenAmount:
                formattedDiscountAmount.dollarDiscountAmount,
            })}
          </PromotionBannerHeader>
          <PromotionBannerSubHeader>
            {t(
              '{dollarDiscountAmount} Minimum Order!',
              formattedDiscountAmount
            )}
          </PromotionBannerSubHeader>
        </React.Fragment>
      )
    case promotionTypes.totalOrder:
      return (
        <React.Fragment>
          <PromotionBannerHeader>
            {getBannerHeaderText({
              t,
              resultAmountType,
              formattedDiscountAmount,
            })}
          </PromotionBannerHeader>
          <PromotionBannerSubHeader>
            {resultAmountType === amountTypes.percentage
              ? t(
                  '{percentageDiscountAmount} Off Total Order!',
                  formattedDiscountAmount
                )
              : t(
                  '{dollarDiscountAmount} Off Total Order!',
                  formattedDiscountAmount
                )}
          </PromotionBannerSubHeader>
        </React.Fragment>
      )
    case promotionTypes.freeItem:
      return (
        <PromotionBannerHeader>
          {t('Free Item!', formattedDiscountAmount)}
        </PromotionBannerHeader>
      )
    case promotionTypes.deliveryFree: {
      const isFree = promotion.resultAmount === 0
      return isFree ? (
        <React.Fragment>
          <PromotionBannerHeader>{t('Free Delivery!')}</PromotionBannerHeader>
        </React.Fragment>
      ) : (
        <React.Fragment>
          <PromotionBannerHeader>
            {getBannerHeaderText({
              t,
              resultAmountType,
              formattedDiscountAmount,
            })}
          </PromotionBannerHeader>
          <PromotionBannerSubHeader>
            {resultAmountType === amountTypes.percentage
              ? t(
                  '{percentageDiscountAmount} Off Delivery Charges!',
                  formattedDiscountAmount
                )
              : t(
                  '{dollarDiscountAmount} Off Delivery Charges!',
                  formattedDiscountAmount
                )}
          </PromotionBannerSubHeader>
        </React.Fragment>
      )
    }
    case promotionTypes.discountNextItem:
      return (
        <React.Fragment>
          <PromotionBannerHeader>
            {getBannerHeaderText({
              t,
              resultAmountType,
              formattedDiscountAmount,
            })}
          </PromotionBannerHeader>
          <PromotionBannerSubHeader>
            {resultAmountType === amountTypes.percentage
              ? t(
                  '{percentageDiscountAmount} Off Next Item!',
                  formattedDiscountAmount
                )
              : t(
                  '{dollarDiscountAmount} Off Next Item!',
                  formattedDiscountAmount
                )}
          </PromotionBannerSubHeader>
        </React.Fragment>
      )
    default:
      return <React.Fragment />
  }
}

// Default styled components converted to regular components with Tailwind classes
export const DefaultPromotionBannerHeader: React.FC<{
  color?: string
  children?: React.ReactNode
}> = ({ color, children }) => (
  <H4
    normalised
    bold
    textAlign="center"
    className={cn(
      'text-[30px] md:text-[38px]',
      color ? `text-[${color}]` : 'text-inherit'
    )}
  >
    {children}
  </H4>
)

export const DefaultPromotionBannerSubHeader: React.FC<{
  children?: React.ReactNode
}> = ({ children }) => (
  <P normalised className="mt-0 text-inherit md:mt-2">
    {children}
  </P>
)

const PromotionCard = ({
  promotion,
  showDefaultImage,
}: {
  promotion: Promotion
  showDefaultImage: boolean
}) => {
  const { formatMoney } = useAppContext()
  const { t } = useTranslate()

  // Dynamic height based on showDefaultImage
  const cardHeight = showDefaultImage
    ? 'h-[200px] md:h-[240px]' // Approximate values for mobile/desktop heights
    : 'h-[120px] md:h-[140px]' // Approximate values for mobile/desktop hide heights

  return (
    <Card
      className={cn(
        'w-full overflow-hidden whitespace-normal rounded-2xl p-0',
        cardHeight
      )}
    >
      <div className={cn('flex h-full flex-col', 'hover:cursor-pointer')}>
        {showDefaultImage && (
          <div
            className={cn(
              'flex flex-col items-center justify-center',
              'h-[100px] flex-none md:h-[120px]',
              'text-button-text',
              'bg-button'
            )}
          >
            {getPromotionBanner({ t, promotion, formatMoney })}
          </div>
        )}
        <div
          className={cn(
            'bg-container flex flex-col justify-between',
            'w-full flex-1 p-5'
          )}
        >
          <H4 textAlign="left" normalised line={2} className="text-headings">
            {promotion.name}
          </H4>
          <div
            className={cn('flex flex-row items-center justify-end', 'self-end')}
          >
            <Button variant="ghost" className="text-button mr-1 font-bold">
              {t('View Details')}
            </Button>
            <ArrowRightIcon className="text-button" />
          </div>
        </div>
      </div>
    </Card>
  )
}

export default PromotionCard
